import {DomainStore} from 'shared/store';
import {observable, action, computed} from 'mobx';
import {v4 as uuidv4} from 'uuid';
import {locale} from 'shared/core';
import {auth, endpoints, types} from 'main/tools';
import {InterviewSchedule} from 'main/stores/interview_schedules';
import User from 'main/stores/users/User';
import _ from 'lodash';

function localizedTime({english, french}) {
  return locale === 'fr' ? french : english;
}

function defaultBusinessHourWindows() {
  return [0, 1, 2, 3, 4, 5, 6].map(day => ({
    id: uuidv4(),
    dayOfWeek: day,
    startTimeString: [1, 2, 3, 4, 5].includes(day) ? localizedTime({english: '9:00am', french: '09:00'}) : '',
    endTimeString: [1, 2, 3, 4, 5].includes(day) ? localizedTime({english: '5:00pm', french: '17:00'}) : ''
  }));
}

function withBlankDays(windows) {
  if (_.isEmpty(windows)) return defaultBusinessHourWindows();

  const result = [...windows];

  [0, 1, 2, 3, 4, 5, 6].forEach(day => {
    if (!result.some(w => w.dayOfWeek === day)) {
      result.push({id: uuidv4(), dayOfWeek: day, startTimeString: '', endTimeString: ''});
    }
  });

  return result;
}

const STEP_CONFIG = {
  interview_details: {
    fields: ['calendarEventName', 'templateName', 'eventDescription', 'duration', 'location', 'address', 'phoneNumber'],
    endpoint: endpoints.INTERVIEW_SCHEDULE.UPDATE_DETAILS
  },
  interviewers: {
    fields: ['meetingHost', 'interviewers'],
    endpoint: endpoints.INTERVIEW_SCHEDULE.UPDATE_INTERVIEWERS
  },
  availability: {
    fields: [
      'relativeEndDateUnit', 'relativeEndDateValue',
      'minimumNoticeValue', 'minimumNoticeUnit',
      'eventBuffer', 'timezone', 'availabilityWindows'
    ],
    endpoint: endpoints.INTERVIEW_SCHEDULE.UPDATE_AVAILABILITY
  },
  invitation_email: {
    fields: ['emailSubject', 'emailRichText', 'emailLexicalState'],
    endpoint: endpoints.INTERVIEW_SCHEDULE.UPDATE_EMAIL
  },
  review: {
    fields: [],
    endpoint: endpoints.INTERVIEW_SCHEDULE.CONFIGURE
  }
};

class InterviewScheduleEditFlowState {
  store = new DomainStore();
  history;
  match;

  @observable interviewSchedule = {};
  @observable users = [];
  @observable errors = {};

  receiveProps({history, match}) {
    this.history = history;
    this.match = match;
  }

  @action async load() {
    await this.store._compose([
      endpoints.INTERVIEW_SCHEDULE.with(this.match.params.id),
      endpoints.USERS.ACTIVE
    ]);

    this.interviewSchedule = new InterviewSchedule(this.store._getSingle(types.INTERVIEW_SCHEDULE));

    this.users = this.store._getAll(types.USER, User);
  }

  @action async onStepChanged({location}) {
    switch (location) {
      case 'interviewers': {
        if (_.isEmpty(this.interviewSchedule.interviewers)) {
          this.interviewSchedule.addInterviewer(_.find(this.users, {id: auth.user.id}));
        }
        if (!this.interviewSchedule.meetingHost) {
          this.interviewSchedule.merge({meetingHost: _.find(this.users, {id: auth.user.id})});
        }
        break;
      }
      case 'availability':
        if (this.interviewSchedule.relativeEndDateValue == null) {
          this.interviewSchedule.merge({
            relativeEndDateValue: 1,
            relativeEndDateUnit: 'weeks'
          });
        }
        if (this.interviewSchedule.minimumNoticeUnit == null) {
          this.interviewSchedule.merge({minimumNoticeUnit: 'hours'});
        }
        if (!this.interviewSchedule.timezone) {
          this.interviewSchedule.merge({timezone: Intl.DateTimeFormat().resolvedOptions().timeZone});
        }
        this.interviewSchedule.merge({
          availabilityWindows: withBlankDays(this.interviewSchedule.availabilityWindows)
        });
        break;
      default:
        break;
    }
  }

  @action async onStepSubmitted({location}) {
    const submitted = await this.submitStep(location);

    if (submitted && location === 'review') {
      this.history.push('/interview-schedules');
    }

    return submitted;
  }

  @action async submitStep(location) {
    const {fields, endpoint} = STEP_CONFIG[location];

    const {model, errors} = await this.store.patch(
      endpoint.with(this.match.params.id),
      types.INTERVIEW_SCHEDULE,
      this.interviewSchedule.pick(fields)
    );

    this.errors = errors;
    if (!model) return false;

    this.interviewSchedule.update(model);
    return true;
  }

  @computed get interviewersMissingConnections() {
    const users = this.interviewSchedule.interviewers.filter(u => u.calendarAppConnected === false);

    if (this.interviewSchedule.meetingHost?.meetingAppConnected === false) {
      users.push(this.interviewSchedule.meetingHost);
    }

    return _.uniqBy(users, 'id');
  }

  @computed get currentUserMissingConnections() {
    return _.some(this.interviewersMissingConnections, {id: auth.user.id});
  }

  @computed get otherUsersMissingConnections() {
    return _.some(this.interviewersMissingConnections, u => u.id !== auth.user.id);
  }
}

export default InterviewScheduleEditFlowState;
